import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Button, TextBox } from "../../../components";
import { useNavigate, useParams } from "react-router-dom";
import useTendering from "../../../hooks/useTendering";
import useCommon from "../../../hooks/useCommon";
import useQuotation from "../../../hooks/useQuotation";
import {
  formatNumber,
  numberToOrdinal,
  dataURLtoFile,
} from "../../../helpers/common.helper";
import DocumentUploader from "../../../components/common/DocumentUploader";
import { ReactComponent as TrashSvg } from "../../../assets/admin/icons/trash.svg";
import { ObjectId } from "bson";
import { toast } from "react-toastify";
import SignatureModal from "./SignatureModal";

const OwnerContract = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(undefined);
  const { getById, getSignedUrl, createContract } = useTendering();
  const { getUsersAndCompanies } = useCommon();
  const { getQuotation } = useQuotation();
  const [signatureFile, setSignatureFile] = useState(undefined);
  const [signaturePreview, setSignaturePreview] = useState(undefined);
  const [stampFile, setStampFile] = useState(undefined);
  const [stampPreview, setStampPreview] = useState(undefined);
  const [isDisplaySignatureModal, setIsDisplaySignatureModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const tendering = await getById(id);

    const winner = tendering.participants.find((p) => p.won === true);
    const winnerId = winner.user;
    const ownerId = tendering.company ?? tendering.createdBy;

    const userResult = await getUsersAndCompanies([ownerId, winnerId]);

    const winnerDetail = userResult.find((u) => u._id === winnerId);
    const ownerDetail = userResult.find((u) => u._id === ownerId);

    // * Get quotation
    const quotation = await getQuotation(winner.quotation.submissions.at(-1));

    setDetail({
      name: tendering.name,
      owner: ownerDetail.name,
      participant: winnerDetail.name,
      total: formatNumber(quotation.grandTotal, ""),
      terms: [
        formatNumber(quotation.advancedPayment.total, ""),
        ...quotation.paymentTerms.map((pt) => formatNumber(pt.subTotal, "")),
      ],
    });
  };

  const handleValidate = (values) => {
    const { name, owner } = values;

    let errors = {};

    if (!name) errors.name = "Required";
    if (!owner) errors.owner = "Required";

    return errors;
  };

  const _uploadFile = async (key, file) => {
    const signedUrl = await getSignedUrl(key, file.type);

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadResponse.status !== 200) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!signatureFile) {
      toast.error("Signature not found");
      return;
    }

    if (!stampFile) {
      toast.error("Stamp not found");
      return;
    }

    // * Upload signature and stamp
    const signatureId = new ObjectId();
    const signatureKey = `tenderings/${id}/contract/owner/signatures/${signatureId}`;

    if (!(await _uploadFile(signatureKey, signatureFile))) {
      toast.error("Unable to upload signature");
      return;
    }

    const stampId = new ObjectId();
    const stampKey = `tenderings/${id}/contract/owner/stamps/${stampId}`;

    if (!(await _uploadFile(stampKey, stampFile))) {
      toast.error("Unable to upload stamp");
      return;
    }

    const _body = {
      name: values.name,
      owner: {
        name: values.owner,
        signature: {
          _id: signatureId,
          key: signatureKey,
          name: signatureFile.name,
          mimeType: signatureFile.type,
          size: signatureFile.size,
        },
        stamp: {
          _id: stampId,
          key: stampKey,
          name: stampFile.name,
          mimeType: stampFile.type,
          size: stampFile.size,
        },
      },
      contractor: {
        name: values.participant,
      },
    };

    await createContract(id, _body);

    toast.success("Contract sent");

    navigate(`/tenderings/${id}`);
  };

  if (!detail) return <>Loading</>;

  return (
    <Formik
      initialValues={detail}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      <Form className="tendering-contract">
        <h3>Create Contract</h3>
        <div className="tendering-contract__form">
          <h4>Information</h4>
          <TextBox name="name" label="Project name" />
          <TextBox name="owner" label="Owner's name" />
          <TextBox
            name="participant"
            label="Participant's name"
            disabled={true}
          />
          <div className="tendering-contract__form__separator" />
          <h4>Payment Terms</h4>
          {detail.terms.map((_t, index) => {
            let displayName = "Advanced";

            if (index > 0) {
              displayName = numberToOrdinal(index);
            }

            if (detail.terms.length === index + 1) {
              displayName = "Final";
            }

            return (
              <TextBox
                name={`terms[${index}]`}
                label={`${displayName} payment`}
                disabled={true}
              />
            );
          })}
          <TextBox
            name="total"
            label="Payment Total (VAT and/or WHT Include)"
            disabled={true}
          />
          <div className="tendering-contract__form__separator" />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h4>Signature</h4>
            <Button
              label="Create a signature"
              variant={"secondary"}
              size="small"
              onClick={() => setIsDisplaySignatureModal(true)}
            />
          </div>
          <DocumentUploader
            name="signature"
            accept="image/*"
            multiple={false}
            onChange={(files) => {
              if (!files || files.length === 0) return;

              setSignatureFile(files.at(-1));

              const fr = new FileReader();
              fr.onload = function () {
                setSignaturePreview(fr.result);
              };
              fr.readAsDataURL(files.at(-1));
            }}
          />
          {signaturePreview && (
            <div className="tendering-contract__form__image-preview">
              <img
                className="tendering-contract__form__image-preview__image"
                src={signaturePreview}
                height="120px"
              />
              <TrashSvg
                className="tendering-contract__form__image-preview__delete"
                width={16}
                height={16}
                onClick={() => {
                  setSignatureFile(undefined);
                  setSignaturePreview(undefined);
                }}
              />
            </div>
          )}
          <div className="tendering-contract__form__separator" />
          <h4>Stamp</h4>
          <DocumentUploader
            name="stamp"
            accept="image/*"
            onChange={(files) => {
              if (!files || files.length === 0) return;

              setStampFile(files.at(-1));

              const fr = new FileReader();
              fr.onload = function () {
                setStampPreview(fr.result);
              };
              fr.readAsDataURL(files.at(-1));
            }}
          />
          {stampPreview && (
            <div className="tendering-contract__form__image-preview">
              <img
                className="tendering-contract__form__image-preview__image"
                src={stampPreview}
                height="120px"
              />
              <TrashSvg
                className="tendering-contract__form__image-preview__delete"
                width={16}
                height={16}
                onClick={() => {
                  setStampFile(undefined);
                  setStampPreview(undefined);
                }}
              />
            </div>
          )}
        </div>
        <div className="tendering-contract__footer">
          <Button label="Cancel" variant={"secondary"} />
          <Button label="Send Contract" type="submit" />
        </div>
        {isDisplaySignatureModal && (
          <SignatureModal
            onSave={(data) => {
              const file = dataURLtoFile(data, "signature.png");

              setSignatureFile(file);
              setSignaturePreview(data);
              setIsDisplaySignatureModal(false);
            }}
            onClose={() => {
              setIsDisplaySignatureModal(false);
            }}
          />
        )}
      </Form>
    </Formik>
  );
};

OwnerContract.propTypes = {};

export default OwnerContract;
